import { language } from '../i18n/language.js';
import { DATA_EN } from '../i18n/data/index.js';
import { EN } from '../i18n/en/index.js';

/**
 * Text for the DOM interface, in the player's language (ADR 0069).
 *
 * The French phrase is the key: in English it is looked up in the catalogue
 * (`i18n/en/`), and a phrase not translated yet shows in French rather than
 * nothing, noted for `shefiraEnglish()`.
 *
 * A `context` tells two senses of one French word apart: `uiText('Retour',
 * 'key')` reads `key|Retour` first (the Backspace key), then `Retour`.
 *
 * The interface font has the French accents and punctuation since
 * `scripts/build-ui-font.py` drew them, so French shows as written.
 */
export function uiText(value: string, context?: string): string {
  if (language() === 'en') {
    // A word whose sense depends on where it stands (« Retour » the key, « Retour » the button).
    const english =
      (context === undefined ? undefined : EN[`${context}|${value}`]) ?? EN[value] ?? DATA_EN[value];
    if (english !== undefined) return english;
    // Game data already put in English at start (`localize-data.ts`).
    if (DATA_ENGLISH.has(value)) return value;
    // Already in the player's language: a `uiFormat` result handed to a helper that translates.
    if (formatted.has(value)) return value;
    noteMissing(value);
  }
  return value;
}

/** Every English text of the game data, so a name read after `localize-data.ts` is left be. */
const DATA_ENGLISH = new Set(Object.values(DATA_EN));

/** What `uiFormat` gave lately, so a helper that calls `uiText` on it again leaves it be. */
const formatted = new Set<string>();

/**
 * A phrase with values in it: the template is translated, then `{name}`
 * placeholders are filled. Values go in as given: translate a game name
 * before passing it, never a player's name.
 */
export function uiFormat(
  template: string,
  values: Readonly<Record<string, string | number>>,
): string {
  const result = uiText(template).replace(/\{(\w+)\}/g, (whole, key: string) => {
    const value = values[key];
    return value === undefined ? whole : String(value);
  });
  if (formatted.size > 500) formatted.clear();
  formatted.add(result);
  return result;
}

/** Phrases met without their English, for the console audit in development. */
const missing = new Set<string>();

function noteMissing(value: string): void {
  // Numbers, a key's letter, a clock time and empty labels need nothing.
  if (!/\p{L}{3}/u.test(value) || /^\d{1,2}:\d{2}/.test(value) || missing.has(value)) return;
  missing.add(value);
}

if (import.meta.env.DEV && typeof window !== 'undefined') {
  Object.assign(window, {
    shefiraEnglish: (): string[] => {
      const list = [...missing].sort();
      console.info(`${String(list.length)} phrase(s) without English:\n${list.join('\n')}`);
      return list;
    },
  });
}

/** The attributes a player reads or hears (a screen reader): translated like the text. */
const READ_ATTRIBUTES = ['placeholder', 'title', 'aria-label', 'alt'] as const;

/**
 * Markup written in French, translated node by node: each text between tags
 * and each read attribute is looked up on its own, its spacing collapsed,
 * so a template needs no change to speak English. Tags, classes and URLs
 * are left alone.
 */
export function uiHtml(markup: string): string {
  const template = document.createElement('template');
  template.innerHTML = markup;
  translateTree(template.content);
  return template.innerHTML;
}

function translateTree(root: DocumentFragment | Element): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    const raw = node.nodeValue ?? '';
    const phrase = raw.replace(/\s+/g, ' ').trim();
    if (phrase === '') continue;
    const lead = /^\s/.test(raw) ? ' ' : '';
    const tail = /\s$/.test(raw) ? ' ' : '';
    node.nodeValue = `${lead}${uiText(phrase)}${tail}`;
  }
  for (const element of root.querySelectorAll('*')) {
    for (const name of READ_ATTRIBUTES) {
      const value = element.getAttribute(name);
      if (value !== null && value.trim() !== '') element.setAttribute(name, uiText(value));
    }
  }
}

/** The locale numbers and dates are written in, after the language. */
function locale(): string {
  return language() === 'en' ? 'en-US' : 'fr-FR';
}

/** A whole number, grouped the player's way (« 1 250 » or « 1,250 »). */
export function uiNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return value.toLocaleString(locale(), options);
}

/** A decimal with a fixed number of digits, comma in French, point in English. */
export function uiDecimal(value: number, digits: number): string {
  const text = value.toFixed(digits);
  return language() === 'fr' ? text.replace('.', ',') : text;
}

/** A percentage already scaled to 0–100: « 30 % » in French, « 30% » in English. */
export function uiPercent(value: number | string): string {
  const shown = typeof value === 'number' ? uiNumber(value) : value;
  return language() === 'fr' ? `${shown} %` : `${shown}%`;
}

/** A price in cents, the shop's currency written the player's way (« 4,99 € », « €4.99 »). */
export function uiPrice(cents: number, currency = 'eur'): string {
  return new Intl.NumberFormat(locale(), {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

export function uiDate(at: Date | number | string, options: Intl.DateTimeFormatOptions): string {
  return new Date(at).toLocaleDateString(locale(), options);
}

export function uiTime(at: Date | number | string, options: Intl.DateTimeFormatOptions): string {
  return new Date(at).toLocaleTimeString(locale(), options);
}
